import axios from 'axios';

// Create axios instance with base URL
const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '/api/admin/';
console.log('API_BASE_URL', API_BASE_URL);
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to track if token error popup is shown
let isTokenErrorPopupShown = false;

// Flag to track auth error state
let isAuthError = false;

// Token management functions
export const setAuthToken = (token, remember = true) => {
  if (token) {
    // Store token in storage based on remember preference
    if (remember) {
      localStorage.setItem('access_token', token);
    } else {
      sessionStorage.setItem('access_token', token);
    }
    
    // Update axios headers
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  return token;
};

export const clearAuthToken = () => {
  // Remove from both storages to be safe
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token');
  
  // Remove from axios headers
  delete axiosInstance.defaults.headers.common['Authorization'];
};

export const getAuthToken = () => {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

// Add request interceptor to include token in requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip token for login, signup, etc.
    if (!config.url.includes('/auth/login') &&
        !config.url.includes('/auth/reset-password') &&
        !config.url.includes('/auth/create-new-password') &&
        !config.url.includes('/auth/verify-token') && 
        !config.url.includes('/oauth/signin')) {
      
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // If we're in an auth error state, prevent new requests
    if (isAuthError) {
      // Create a canceled request error
      const error = new Error('Request canceled due to authentication error');
      error.isAuthError = true;
      return Promise.reject(error);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401 && !isTokenErrorPopupShown) {
      isTokenErrorPopupShown = true;
      isAuthError = true;
      
      // Clear user data
      clearAuthToken();
      localStorage.removeItem('user');
      
      // Only redirect if not already on the login page
      if (window.location.pathname !== '/auth/login') {
        window.location.replace('/auth/login');
      }
      
      // Reset flag after redirect
      setTimeout(() => {
        isTokenErrorPopupShown = false;
      }, 1000);
    }
    
    // Add isAuthError flag to error object
    if (isAuthError) {
      error.isAuthError = true;
    }
    
    return Promise.reject(error);
  }
);

// Function to reset auth error state
export const resetAuthErrorState = () => {
  isAuthError = false;
};

// Add method functions to the axios instance
const api = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data = {}, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data = {}, config = {}) => axiosInstance.put(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
  patch: (url, data = {}, config = {}) => axiosInstance.patch(url, data, config),
  
  // Auth methods
  setAuthToken,
  clearAuthToken,
  getAuthToken,
  
  // Login helper
  login: async (credentials) => {
    try {
      const response = await axiosInstance.post('/auth/login/', credentials);
      const { access_token } = response.data;
      if (access_token) {
        setAuthToken(access_token, credentials.remember);
      }
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  
  // Logout helper
  logout: async () => {
    try {
      await axiosInstance.post('/auth/logout/');
      clearAuthToken();
    } catch (error) {
      clearAuthToken();
      return Promise.reject(error);
    }
  },

  // Invoice status update helper
  updateInvoiceStatus: async (invoiceId, statusData) => {
    try {
      const response = await axiosInstance.patch(`/invoice/${invoiceId}/details/`, statusData);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // Share invoice helper
  shareInvoice: async (invoiceId) => {
    try {
      const response = await axiosInstance.post(`/invoice/${invoiceId}/share/`);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default api;